import type { CalendarEntry } from "./dividends";

export type MonthTileStatus = "PAID" | "ANNOUNCED" | "FORECAST" | "MIXED";

export interface YearlyTotal {
  year: number;
  totalEur: number;
  pastEur: number;
  forecastEur: number;
  yoyPercent: number | null;
}

export interface MonthTileData {
  month: string; // "YYYY-MM"
  totalEur: number;
  positionCount: number;
  status: MonthTileStatus;
  entries: CalendarEntry[]; // referenz auf original-Einträge für Modal
}

function entryValueEur(e: CalendarEntry): number {
  if (e.kind === "PAST") return e.actualTotalEur ?? 0;
  return e.estimatedTotalEur ?? 0;
}

function entryYear(e: CalendarEntry): number {
  return Number.parseInt(e.payDate.slice(0, 4), 10);
}

function entryMonth(e: CalendarEntry): string {
  return e.payDate.slice(0, 7);
}

export function aggregateByYear(entries: CalendarEntry[]): YearlyTotal[] {
  const map = new Map<number, YearlyTotal>();
  for (const e of entries) {
    const year = entryYear(e);
    let y = map.get(year);
    if (!y) {
      y = { year, totalEur: 0, pastEur: 0, forecastEur: 0, yoyPercent: null };
      map.set(year, y);
    }
    const v = entryValueEur(e);
    if (e.kind === "PAST") y.pastEur += v;
    else y.forecastEur += v;
    y.totalEur = y.pastEur + y.forecastEur;
  }
  const sorted = [...map.values()].sort((a, b) => a.year - b.year);
  // YoY berechnen: nach Sort haben wir aufsteigend, davor prüfen
  for (let i = 1; i < sorted.length; i++) {
    const curr = sorted[i];
    const prev = sorted[i - 1];
    if (curr && prev && curr.year === prev.year + 1 && prev.totalEur > 0) {
      curr.yoyPercent = (curr.totalEur - prev.totalEur) / prev.totalEur;
    }
  }
  return sorted;
}

export function monthTileStatus(entries: CalendarEntry[]): MonthTileStatus {
  const hasPast = entries.some((e) => e.kind === "PAST");
  const forecasts = entries.filter((e) => e.kind === "FORECAST");
  if (hasPast && forecasts.length > 0) return "MIXED";
  if (hasPast) return "PAID";
  // Nur FORECAST übrig
  if (forecasts.some((e) => e.source === "EXTRAPOLATED")) return "FORECAST";
  return "ANNOUNCED"; // YAHOO_ANNOUNCED oder MANUAL
}

export function aggregateByMonth(
  entries: CalendarEntry[],
  year: number,
  today: Date,
): { upcoming: MonthTileData[]; past: MonthTileData[] } {
  const yearEntries = entries.filter((e) => entryYear(e) === year);
  const byMonth = new Map<string, CalendarEntry[]>();
  for (const e of yearEntries) {
    const m = entryMonth(e);
    const bucket = byMonth.get(m);
    if (bucket) bucket.push(e);
    else byMonth.set(m, [e]);
  }

  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const upcoming: MonthTileData[] = [];
  const past: MonthTileData[] = [];

  for (const [month, monthEntries] of byMonth.entries()) {
    const status = monthTileStatus(monthEntries);
    const totalEur = monthEntries.reduce((s, e) => s + entryValueEur(e), 0);
    const tile: MonthTileData = {
      month,
      totalEur,
      positionCount: monthEntries.length,
      status,
      entries: monthEntries,
    };
    // Vergangenheit vs Zukunft: strikt nach Monat vs currentMonth,
    // bei currentMonth entscheidet der Status (PAID/MIXED → past, sonst upcoming)
    if (month < currentMonth) past.push(tile);
    else if (month > currentMonth) upcoming.push(tile);
    else {
      if (status === "PAID" || status === "MIXED") past.push(tile);
      else upcoming.push(tile);
    }
  }

  past.sort((a, b) => a.month.localeCompare(b.month));
  upcoming.sort((a, b) => a.month.localeCompare(b.month));
  return { upcoming, past };
}
